import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { selfInvoiceSaved, selfInvoiceSent } from "store/actions/selfInvoiceActions";
//#endregion
//#region types
import { SelfInvoiceWizardFooterProps } from "models/propTypes/selfInvoicePropTypes";
import { TranslationKeys } from "translations";
import { StyleEnums } from "models/enums";
import { InvoiceFormTypes } from "models/formTypes";
import { HTMLConstants } from "utils/constants";
//#endregion
//#region components
import { WizardFormFooter } from "components/common";
import { useInvoiceService, useSelfInvoiceService } from "serviceHooks";
import SelfInvoiceWizardMoreActions from "./wizardMoreActions";
import { Portal } from "@mui/material";
import { useFormSubmitHelpers } from "components/common/wintForms";
import useSelfInvoiceFormValidations from "../forms/useValidationSchema";
import { useLocation, useNavigate } from "react-router-dom";
//#endregion
import { UrlHelper } from "utils/helpers";

const SelfInvoiceWizardFooter: React.FC<SelfInvoiceWizardFooterProps> = ({
  invoiceId,
  form,
  invoice,
  reloadInvoice,
  wizardProps,
}) => {
  const dispatch = useDispatch();
  const selfInvoiceService = useSelfInvoiceService();
  const invoiceService = useInvoiceService();

  const location = useLocation();
  const navigate = useNavigate();

  const footerMount = document.getElementById(HTMLConstants.EntityCreationLayoutFooterPortalId);

  const { submitForm } = useFormSubmitHelpers();
  const { getValidationSchema } = useSelfInvoiceFormValidations();

  const onInvoiceFormSubmit = (asDraft: boolean, form: InvoiceFormTypes.SelfInvoice) => {
    const invoicePost = selfInvoiceService.convertFormToAPIRequest(form, invoice);

    return invoiceService.createOrUpdateInvoice(form.relatedInvoiceId, invoicePost, asDraft);
  };

  const submitInvoice = async (asDraft: boolean) => {
    return submitForm(form, onInvoiceFormSubmit.bind(null, asDraft ?? false), getValidationSchema(asDraft));
  };

  const onAccept = async () => {
    const customerInvoiceId = await submitInvoice(false);
    if (customerInvoiceId) {
      try {
        await invoiceService.markAsSent(customerInvoiceId);
        await selfInvoiceService.markAsSubmitted(invoiceId);
      } catch (e) {
        dispatch(selfInvoiceSaved({ ...invoice, draftInvoiceId: customerInvoiceId }));
        form.setFieldValue("relatedInvoiceId", customerInvoiceId);
        throw e;
      }
    }

    return customerInvoiceId;
  };

  const onAcceptedSuccess = (customerInvoiceId: number) => {
    dispatch(selfInvoiceSent(invoiceId));
    if (location.pathname.includes("/SelfInvoice")) {
      navigate(UrlHelper.formatPath(`/Invoice/${customerInvoiceId}`), {
        replace: true,
        state: { background: location },
      });
    } else {
      reloadInvoice();
    }
  };

  const onSave = () => submitInvoice(true);

  const onSaveSuccess = (customerInvoiceId: number) => {
    if (invoice.draftInvoiceId === undefined) {
      dispatch(selfInvoiceSaved({ ...invoice, draftInvoiceId: customerInvoiceId }));
      form.setFieldValue("relatedInvoiceId", customerInvoiceId);
      reloadInvoice();
    }
  };

  return (
    <Portal container={footerMount}>
      <WizardFormFooter
        actions={[
          {
            text: TranslationKeys.Common.Save,
            onClick: onSave,
            successMessage: TranslationKeys.Invoice.SaveSuccessMessage,
            onActionSuccess: onSaveSuccess,
          },
          {
            text: TranslationKeys.Common.Approve,
            onClick: onAccept,
            type: StyleEnums.DisplayType.Success,
            successMessage: TranslationKeys.Invoice.ApproveSuccessMessage,
            onActionSuccess: onAcceptedSuccess,
          },
        ]}
        moreActions={
          <SelfInvoiceWizardMoreActions
            id={invoiceId}
            reloadHandler={reloadInvoice}
            draftCustomerInvoiceId={form.values.relatedInvoiceId}
          />
        }
        wizardProps={wizardProps}
      />
    </Portal>
  );
};

export default SelfInvoiceWizardFooter;
